import React from "react";
import JSZip from "jszip";
import "./DocumentViewer.css";

/* ---------- Icon Components ---------- */
const IconDoc = ({ className = "me-2", style }) => (
  <svg className={className} style={style} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
  </svg>
);

const IconImage = ({ className = "me-2" }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <path d="M21 15l-5-5L5 21" />
  </svg>
);

const IconEye = ({ className = "" }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

/* ---------- Document Viewer Component ---------- */
export default function DocumentViewer({
  documents,
  filteredDocuments,
  loadingDocuments,
  selectedDocId,
  setSelectedDocId,
  selectedDoc,
  documentSearchQuery,
  onDocumentSearchChange,
  setIsSplitScreen,
  inboxOpen,
  setInboxOpen,
  onOpenPreview,
  inlinePreviewUrl,
  inlinePreviewLoading,
  isSplitScreen,
  className = "",
  hideSplitToggle = false,
}) {
  const onDocumentSelect = (uniqueKey) => {
    console.log('Document clicked - ID:', uniqueKey);
    setSelectedDocId(uniqueKey);
  };

  /* State to track inbox status before split screen */
  const previousInboxState = React.useRef(inboxOpen);

  const onToggleSplitScreen = () => {
    if (!isSplitScreen) {
      previousInboxState.current = inboxOpen;
      setInboxOpen(false);
      setIsSplitScreen(true);
    } else {
      setIsSplitScreen(false);
      setInboxOpen(previousInboxState.current);
    }
  };

  const onDocumentDownload = (doc) => {
    console.log('Download clicked for:', doc.name);
    const link = document.createElement('a');
    let downloadUrl = doc.download_url;
    if (downloadUrl.startsWith('http://')) {
      downloadUrl = downloadUrl.replace('http://', 'https://');
    }
    link.href = downloadUrl;
    link.download = doc.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const [isDownloadingZip, setIsDownloadingZip] = React.useState(false);

  const onDownloadAll = async () => {
    console.log('Download All as ZIP clicked - Documents:', documents.length);
    if (documents.length === 0) return;
    
    setIsDownloadingZip(true);
    
    try {
      const zip = new JSZip();
      
      // Fetch all documents and add them to the zip
      const promises = documents.map(async (doc) => {
        try {
          let downloadUrl = doc.download_url;
          if (downloadUrl.startsWith('http://')) {
            downloadUrl = downloadUrl.replace('http://', 'https://');
          }
          
          const response = await fetch(downloadUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch ${doc.name}`);
          }
          
          const blob = await response.blob();
          zip.file(doc.name, blob);
          console.log(`Added ${doc.name} to zip`);
        } catch (error) {
          console.error(`Failed to add ${doc.name} to zip:`, error);
        }
      });
      
      await Promise.all(promises);
      
      // Generate the zip file
      console.log('Generating zip file...');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      
      // Create download link
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `documents_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the object URL
      URL.revokeObjectURL(link.href);
      
      console.log('ZIP file downloaded successfully');
    } catch (error) {
      console.error('Failed to create zip file:', error);
      alert('Failed to download documents as ZIP. Please try again.');
    } finally {
      setIsDownloadingZip(false);
    }
  };

  return (
    <aside className={`document-viewer-area ${isSplitScreen ? 'col-12 col-lg-6' : 'col-12 col-lg-4'} h-100 ${className}`} style={{ paddingLeft: '0px' }}>
      <div className="card document-viewer-card h-100">
        <div className="card-body p-3 pt-2 pb-0 document-viewer-body h-100 overflow-hidden">
          <div className="document-viewer-container h-100 overflow-auto" style={{ position: 'relative' }}>
            {!isSplitScreen && (
              <div className="document-viewer-header">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="card-title my-2 fw-bold">Document Viewer</h5>
                </div>

                <div className="position-relative">
                  <svg
                    className="position-absolute"
                    style={{
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      zIndex: 2,
                      pointerEvents: 'none'
                    }}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.35-4.35" />
                  </svg>
                  <input
                    className="form-control form-control-sm"
                    placeholder="Search documents"
                    value={documentSearchQuery}
                    onChange={(e) => onDocumentSearchChange(e.target.value)}
                    style={{ borderRadius: '8px', padding: '8px 12px', paddingLeft: '36px' }}
                  />
                </div>
              </div>
            )}

            {/* Document Preview Section */}
            <div className={`document-preview-section ${isSplitScreen ? 'h-100 d-flex flex-column' : 'mb-3'}`}>
              <div className="d-flex justify-content-between align-items-center pt-0 py-2">
                <h6 className="mb-0 fw-semibold">Brief Preview</h6>
                {!hideSplitToggle && (
                  <div onClick={(e) => { e.stopPropagation(); onToggleSplitScreen(); }}
                    title={isSplitScreen ? "Exit split screen" : "Split screen view"}
                    aria-label={isSplitScreen ? "Exit split screen" : "Split screen view"}
                    disabled={!selectedDoc} style={{ cursor: selectedDoc ? "pointer" : "default", }}>
                    <button
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        paddingRight: "5px",
                        outline: "none",
                        opacity: selectedDoc ? 1 : 0.5
                      }}
                    >
                      <img
                        src={isSplitScreen ? "/ToggleFullScreenToNormal.png" : "/ToggleFullScreen.png"}
                        alt="toggle-screen"
                        style={{ width: 20, height: 20, objectFit: "cover" }}
                      />
                    </button>
                  </div>
                )}
              </div>
              <div className={`${isSplitScreen ? 'flex-grow-1 overflow-hidden' : 'border rounded p-3 bg-light'} text-center document-preview-box`} style={isSplitScreen ? { height: 'auto', minHeight: 0 } : { minHeight: '450px' }}>
                {inlinePreviewLoading ? (
                  <div className="text-center">
                    <div className="spinner-border spinner-border-sm text-primary mb-2" role="status">
                      <span className="visually-hidden">Loading preview...</span>
                    </div>
                    <div className="small text-muted">Loading preview...</div>
                  </div>
                ) : selectedDoc ? (
                  <div className="w-100 h-100" style={isSplitScreen ? { height: '100%', overflow: 'auto', textAlign: 'left' } : {}}>
                    {(() => {
                      // Standard Preview Logic
                      const fileExtension = selectedDoc.name.split('.').pop().toLowerCase();
                      const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExtension);

                      if (isImage && inlinePreviewUrl) {
                        return (
                          <img
                            src={inlinePreviewUrl}
                            alt={selectedDoc.name}
                            className="img-fluid rounded"
                            style={isSplitScreen
                              ? { width: '125%', height: 'auto', maxWidth: 'none', maxHeight: 'none' }
                              : { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }
                            }
                          />
                        );
                      } else if (fileExtension === 'pdf' && inlinePreviewUrl) {
                        return (
                          <iframe
                            src={`${inlinePreviewUrl}${isSplitScreen ? '#zoom=125' : ''}`}
                            title={selectedDoc.name}
                            className="w-100 h-100 border-0"
                          />
                        );
                      } else {
                        return (
                          <div className="text-muted pt-5">
                            <IconDoc className="mb-2" style={{ width: '48px', height: '48px' }} />
                            <div>Preview not available for this file type</div>
                            <div className="small">Click preview button to open in modal</div>
                          </div>
                        );
                      }
                    })()}
                  </div>
                ) : selectedDoc ? (
                  <div className="text-muted">
                    <IconDoc className="mb-2" style={{ width: '48px', height: '48px' }} />
                    <div>Unable to load preview</div>
                    <div className="small">Click preview button to view</div>
                  </div>
                ) : (
                  <div className="text-muted">
                    <IconDoc className="mb-2" style={{ width: '48px', height: '48px' }} />
                    Select a document to preview
                  </div>
                )}
              </div>
            </div>

            {/* Documents List Section */}
            {
              !isSplitScreen && (
                <div className="documents-list-section mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0 small fw-semibold">Attachments List</h6>
                    <div className="small text-muted">{filteredDocuments.length} of {documents.length} files</div>
                  </div>
                  <div className="document-list-scroll">
                    {loadingDocuments ? (
                      <div className="text-center">
                        <div className="spinner-border spinner-border-sm" role="status">
                          <span className="visually-hidden">Loading documents...</span>
                        </div>
                        <div className="small text-muted mt-2">Loading documents...</div>
                      </div>
                    ) : documents.length === 0 ? (
                      <div className="text-muted small p-3 text-center">No documents found</div>
                    ) : filteredDocuments.length === 0 ? (
                      <div className="text-muted small p-3 text-center">No documents match your search</div>
                    ) : (
                      filteredDocuments.map((d) => {
                        const isSelected = d.uniqueKey === selectedDocId;
                        const fileExtension = d.name.split('.').pop().toLowerCase();
                        const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(fileExtension);

                        return (
                          <div
                            key={d.uniqueKey}
                            className={`document-item d-flex align-items-center p-2 ${isSelected ? "selected" : ""}`}
                            onClick={() => onDocumentSelect(d.uniqueKey)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                onDocumentSelect(d.uniqueKey);
                              }
                            }}
                          >
                            <div className="d-flex align-items-center justify-content-center me-2" style={{ width: 32, height: 32, minWidth: 32 }}>
                              {isImage ? <IconImage className="m-0" /> : <IconDoc className="m-0" />}
                            </div>

                            <div className="flex-grow-1 text-start overflow-hidden">
                              <div className="fw-medium text-truncate small" title={d.name}>{d.name}</div>
                              <div className="small text-muted">
                                {new Date(d.uploaded_at).toLocaleDateString()}
                              </div>
                            </div>

                            <div className="d-flex gap-1 ms-2" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onDocumentDownload(d);
                                }}
                                className="btn btn-sm text-white"
                                style={{ padding: "0.25rem 0.5rem" }}
                                title="Download"
                                aria-label={`Download ${d.name}`}
                              >
                                <img src="/Vector.png" alt="" />
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              )
            }

            {/* Document Details Section */}
            {
              !isSplitScreen && (
                <div className="document-details-section">
                  <div>
                    <label className="form-label small mb-1">
                      File Name <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control form-control-sm mb-2"
                      value={selectedDoc?.name || ""}
                      readOnly
                    />

                    <label className="form-label small mb-1">
                      AI Suggested Document Name <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control form-control-sm mb-2"
                      value={selectedDoc?.ai_suggested_name || ""}
                      readOnly
                    />

                    <label className="form-label small mb-1">
                      Document Category <span className="text-danger">*</span>
                    </label>
                    <input
                      className="form-control form-control-sm mb-2"
                      value={selectedDoc?.category || ""}
                      readOnly
                    />

                    <label className="form-label small mb-1">Document Analysis Details</label>
                    <textarea
                      className="form-control form-control-sm mb-2"
                      rows={20}
                      value={(() => {
                        try {
                          if (selectedDoc?.description) {
                            const descObj = JSON.parse(selectedDoc.description);
                            let fullDescription = '';

                            if (descObj.description_text) {
                              fullDescription += `📝 Description: ${descObj.description_text}\n\n`;
                            }
                            if (descObj.key_points && descObj.key_points.length > 0) {
                              fullDescription += `🔑 Key Points:\n`;
                              descObj.key_points.forEach((point, index) => {
                                fullDescription += `  ${index + 1}. ${point}\n`;
                              });
                              fullDescription += '\n';
                            }
                            if (descObj.extracted_entities && descObj.extracted_entities.length > 0) {
                              fullDescription += `📊 Extracted Entities:\n`;
                              descObj.extracted_entities.forEach((entity, index) => {
                                fullDescription += `  ${index + 1}. ${entity.name}: ${entity.value}\n`;
                              });
                              fullDescription += '\n';
                            }
                            if (descObj.evidence_snippet) {
                              fullDescription += `🔍 Evidence: ${descObj.evidence_snippet}\n\n`;
                            }

                            fullDescription += `📄 Document Info:\n`;
                            if (descObj.pages_processed) fullDescription += `  • Pages Processed: ${descObj.pages_processed}\n`;
                            if (descObj.word_count) fullDescription += `  • Word Count: ${descObj.word_count}\n`;
                            if (descObj.language) fullDescription += `  • Language: ${descObj.language}\n`;
                            if (descObj.confidence !== undefined) fullDescription += `  • Confidence: ${(descObj.confidence * 100).toFixed(1)}%\n`;

                            return fullDescription.trim();
                          }
                          return "";
                        } catch (e) {
                          console.error('Error parsing document description:', e);
                          return selectedDoc?.description || "";
                        }
                      })()}
                      readOnly
                      style={{ resize: 'none' }}
                    />

                    <button
                      className="btn btn-primary btn-sm w-100 mb-2 mt-2"
                      onClick={(e) => {
                        e.preventDefault();
                        onDownloadAll();
                      }}
                      disabled={documents.length === 0 || isDownloadingZip}
                      style={{
                        borderRadius: '6px',
                        padding: '10px 24px',
                        fontWeight: '500',
                        backgroundColor: '#006BB3',
                        borderColor: '#006BB3'
                      }}
                    >
                      {isDownloadingZip ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating ZIP...
                        </>
                      ) : (
                        `Download All as ZIP (${documents.length})`
                      )}
                    </button>
                  </div>
                </div>
              )
            }
          </div>
        </div>
      </div>
    </aside>
  );
}
